import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCcw, Mail, Phone, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/contact`);
      setItems(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Admin · Enquiries";
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-page">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between">
          <div>
            <Link
              to="/"
              data-testid="admin-back-link"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back to site
            </Link>
            <h1 className="mt-2 font-heading text-2xl sm:text-3xl font-bold">
              Enquiries
            </h1>
            <p className="text-white/70 text-sm mt-1">
              Contact form submissions from the website.
            </p>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh-button"
            className="inline-flex items-center gap-2 bg-white text-[#0A192F] px-4 py-2 rounded-md font-medium hover:bg-slate-100 transition-colors"
          >
            <RefreshCcw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {loading && (
          <div
            data-testid="admin-loading"
            className="flex items-center gap-2 text-[#334155]"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        )}

        {error && (
          <div
            data-testid="admin-error"
            className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4"
          >
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div
            data-testid="admin-empty"
            className="bg-white border border-slate-200 rounded-md p-10 text-center text-[#64748B]"
          >
            No enquiries yet. They will appear here once customers submit
            the contact form.
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <ul className="space-y-4" data-testid="admin-list">
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`admin-item-${it.id}`}
                className="bg-white border border-slate-200 rounded-md p-6"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-heading text-lg font-semibold text-[#0A192F]">
                    {it.name}
                  </h3>
                  <span className="text-xs text-[#64748B]">
                    {new Date(it.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-4 text-sm">
                  {it.phone && (
                    <a
                      href={`tel:${it.phone}`}
                      className="inline-flex items-center gap-2 text-[#0A192F] hover:underline"
                    >
                      <Phone className="w-4 h-4" /> {it.phone}
                    </a>
                  )}
                  <a
                    href={`mailto:${it.email}`}
                    className="inline-flex items-center gap-2 text-[#0A192F] hover:underline"
                  >
                    <Mail className="w-4 h-4" /> {it.email}
                  </a>
                </div>
                <p className="mt-4 text-[#334155] leading-relaxed whitespace-pre-wrap">
                  {it.message}
                </p>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
